import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import { Event, TournamentMatch, TournamentGroup } from '../types/Event';
import { API_BASE_URL } from '../config';

import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface Props {
  event: Event;
  onEventUpdated?: (event: Event) => void;
  readOnly?: boolean;
}

interface ParticipantStats {
  id: string;
  name: string;
  played: number;
  won: number;
  lost: number;
  points: number;
}

const TournamentManager: React.FC<Props> = ({ event, onEventUpdated, readOnly = false }) => {
  const [loading, setLoading] = useState(false);
  const [startMode, setStartMode] = useState<'group' | 'knockout'>('group');
  const [groupCount, setGroupCount] = useState<number | string>(event.tournamentData?.config.groupCount || 4);
  const [advancePerGroup, setAdvancePerGroup] = useState<number | string>(event.tournamentData?.config.advancePerGroup || 2);
  const [groupBestOf, setGroupBestOf] = useState<number>(event.tournamentData?.config.groupBestOf || 1);
  const [knockoutBestOf, setKnockoutBestOf] = useState<number>(event.tournamentData?.config.knockoutBestOf || 3);
  const [activeGroupTab, setActiveGroupTab] = useState(0);
  const [activeSection, setActiveSection] = useState<'group' | 'knockout'>(
    event.tournamentData?.stage === 'knockout' ? 'knockout' : 'group'
  );

  // Sync activeSection with stage
  useEffect(() => {
    if (event.tournamentData?.stage === 'knockout') {
      setActiveSection('knockout');
    } else {
      setActiveSection('group');
    }
  }, [event.tournamentData?.stage]);

  const [notification, setNotification] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  // Confirmation state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState<{
    title: string;
    message: string;
    qualifiers: { name: string; info: string }[];
    actionType?: 'generateKnockout';
  } | null>(null);

  // Qualifiers Selection State
  const [selectedQualifiers, setSelectedQualifiers] = useState<Record<string, string[]>>({});

  // Editing state
  const [editingMatch, setEditingMatch] = useState<{ match: TournamentMatch, groupId: string, roundId?: string } | null>(null);
  const [editScores, setEditScores] = useState<{ score1: number | string, score2: number | string, sets?: { score1: number | string, score2: number | string }[] }>({ score1: 0, score2: 0 });
  const [editLocation, setEditLocation] = useState<string>('');
  const [isSavingScore, setIsSavingScore] = useState(false);

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsAdvancePerGroup, setSettingsAdvancePerGroup] = useState<number | string>(event.tournamentData?.config.advancePerGroup || 2);

  // Group name editing state
  const [editingGroupName, setEditingGroupName] = useState<{ groupId: string; name: string } | null>(null);

  const handleUpdateSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/events/${event._id}/tournament/config`,
        { advancePerGroup: settingsAdvancePerGroup },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      onEventUpdated?.(response.data);
      setNotification({ message: '設定已更新', severity: 'success' });
      setSettingsOpen(false);
    } catch (error) {
      console.error('Error updating settings:', error);
      setNotification({ message: '更新失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroupName = async () => {
    if (!editingGroupName) return;
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/events/${event._id}/tournament/group`,
        { groupId: editingGroupName.groupId, name: editingGroupName.name },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      onEventUpdated?.(response.data);
      setNotification({ message: '分組名稱已更新', severity: 'success' });
      setEditingGroupName(null);
    } catch (error) {
      console.error('Error updating group name:', error);
      setNotification({ message: '更新失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/events/${event._id}`
        // No auth header needed for public view usually, but if needed, localStorage.getItem('token')
      );
      onEventUpdated?.(response.data);
      setNotification({ message: '已更新最新數據', severity: 'success' });
    } catch (error) {
      console.error('Error refreshing event:', error);
      setNotification({ message: '更新失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDraw = async () => {
    if (readOnly) return;
    if (!window.confirm('確定要重新抽籤嗎？現有的比賽紀錄將會被清除。')) {
      return;
    }

    setLoading(true);
    try {
      if (startMode === 'group') {
        const response = await axios.post(
          `${API_BASE_URL}/events/${event._id}/tournament/draw`,
          {
            groupCount,
            advancePerGroup,
            groupBestOf,
            knockoutBestOf
          },
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        onEventUpdated?.(response.data);
        setNotification({ message: '抽籤完成', severity: 'success' });
      } else {
        // Direct Knockout
        const response = await axios.post(
          `${API_BASE_URL}/events/${event._id}/tournament/knockout`,
          { directKnockout: true },
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        onEventUpdated?.(response.data);
        setNotification({ message: '淘汰賽生成成功', severity: 'success' });
      }
    } catch (error) {
      console.error('Error generating tournament:', error);
      setNotification({ message: '生成失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKnockout = async () => {
    if (readOnly) return;

    // 1. Prepare Confirmation Data
    let qualifiers: { name: string; info: string }[] = [];
    let title = '';
    let message = '';

    if (event.tournamentData?.stage === 'group') {
      title = '進入淘汰賽階段';
      message = '請確認晉級名單 (如有平局可手動調整):';

      // Initialize Selection with defaults
      const initialSelection: Record<string, string[]> = {};
      if (event.tournamentData?.groups) {
        event.tournamentData.groups.forEach(group => {
          const standings = calculateStandings(group);
          const groupQualifiers = standings.slice(0, event.tournamentData?.config.advancePerGroup || 2);
          initialSelection[group._id] = groupQualifiers.map(p => p.id);
        });
      }
      setSelectedQualifiers(initialSelection);

    } else if (event.tournamentData?.stage === 'knockout') {
      title = '生成下一輪對陣';
      message = '確定要結束本輪比賽並生成下一輪對陣嗎？以下獲勝者將晉級：';

      if (event.tournamentData?.knockout) {
        const currentRound = event.tournamentData.knockout[event.tournamentData.knockout.length - 1];
        if (currentRound) {
          currentRound.matches.forEach(match => {
            if (match.winner) {
              qualifiers.push({ name: getParticipantName(match.winner), info: '晉級' });
            } else if (match.score1 > match.score2) {
              qualifiers.push({ name: getParticipantName(match.player1), info: '晉級' });
            } else if (match.score2 > match.score1) {
              qualifiers.push({ name: getParticipantName(match.player2), info: '晉級' });
            }
          });
        }
      }
    }

    // 3. Open Dialog
    setConfirmDialogData({
      title,
      message,
      qualifiers,
      actionType: 'generateKnockout'
    });
    setConfirmDialogOpen(true);
  };

  const executeKnockoutGeneration = async () => {
    setLoading(true);
    try {
      const payload: any = {};
      if (event.tournamentData?.stage === 'group') {
        payload.manualQualifiers = selectedQualifiers;
      }

      const response = await axios.post(
        `${API_BASE_URL}/events/${event._id}/tournament/knockout`,
        payload,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      onEventUpdated?.(response.data);
      setNotification({ message: '操作成功', severity: 'success' });
      setConfirmDialogOpen(false);
    } catch (error: any) {
      console.error('Error generating knockout:', error);
      // Show error message from backend if available
      const errorMsg = error.response?.data?.message || '生成失敗';
      setNotification({ message: errorMsg, severity: 'error' });
      setConfirmDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (readOnly) return;
    if (!window.confirm('確定要重置比賽嗎？所有分組和比分都將被清除，並回到設定階段。')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/events/${event._id}/tournament/reset`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      onEventUpdated?.(response.data);
      setNotification({ message: '比賽已重置', severity: 'success' });
    } catch (error) {
      console.error('Error resetting tournament:', error);
      setNotification({ message: '重置失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openEditMatchDialog = (match: TournamentMatch, groupId: string, roundId?: string) => {
    if (readOnly) return;
    setEditingMatch({ match, groupId: groupId || '', roundId });

    // Determine bestOf for this match
    let bestOf = match.bestOf;
    if (!bestOf) {
      if (roundId && event.tournamentData?.knockout) {
        const round = event.tournamentData.knockout.find(r => r._id === roundId);
        bestOf = round?.bestOf || event.tournamentData.config.knockoutBestOf || 3;
      } else if (groupId && event.tournamentData?.groups) {
        const group = event.tournamentData.groups.find(g => g._id === groupId);
        bestOf = group?.bestOf || event.tournamentData.config.groupBestOf || 1;
      }
    }
    const currentBestOf = bestOf || 1;

    // Initialize sets
    let initialSets = match.sets || [];
    if (initialSets.length === 0 && currentBestOf > 1) {
      // Pre-fill with empty sets
      const setsNeeded = currentBestOf;
      initialSets = Array(setsNeeded).fill(0).map(() => ({ score1: 0, score2: 0 }));
    }

    setEditScores({
      score1: match.score1,
      score2: match.score2,
      sets: initialSets.map(s => ({ ...s }))
    });
    setEditLocation(match.location || '');
  };

  const handleSaveScore = async () => {
    if (!editingMatch || readOnly) return;

    setIsSavingScore(true);
    const { match, groupId, roundId } = editingMatch;

    try {
      // Update score
      await axios.put(
        `${API_BASE_URL}/events/${event._id}/tournament/match`,
        {
          matchId: match._id,
          groupId: groupId,
          roundId: roundId,
          score1: Number(editScores.score1) || 0,
          score2: Number(editScores.score2) || 0,
          location: editLocation,
          sets: editScores.sets?.map(s => ({
            score1: Number(s.score1) || 0,
            score2: Number(s.score2) || 0
          })),
          bestOf: editScores.sets && editScores.sets.length > 1 ? editScores.sets.length : 1
        },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      // Fetch updated event to sync state
      // Or we can construct optimistic update if we prefer
      const response = await axios.get(
        `${API_BASE_URL}/events/${event._id}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      onEventUpdated?.(response.data);
      setNotification({ message: '分數更新成功', severity: 'success' });
      setEditingMatch(null);
    } catch (error) {
      console.error('Error updating match score:', error);
      setNotification({ message: '更新分數失敗', severity: 'error' });
    } finally {
      setIsSavingScore(false);
    }
  };

  const handleMoveMatch = async (matchId: string, groupId: string, direction: 'up' | 'down') => {
    if (readOnly) return;

    // Find the group and match index
    const group = event.tournamentData?.groups.find(g => g._id === groupId);
    if (!group) return;

    const currentIndex = group.matches.findIndex(m => m._id === matchId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= group.matches.length) return;

    // Reorder logic
    const reorderedMatches = [...group.matches];
    const [movedMatch] = reorderedMatches.splice(currentIndex, 1);
    reorderedMatches.splice(newIndex, 0, movedMatch);

    const matchIds = reorderedMatches.map(m => m._id);

    setLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/events/${event._id}/tournament/matches/reorder`,
        { groupId, matchIds },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      onEventUpdated?.(response.data);
      // setNotification({ message: '比賽順序已更新', severity: 'success' }); // Optional, might be too noisy
    } catch (error) {
      console.error('Error reordering matches:', error);
      setNotification({ message: '更新順序失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Helper to find participant name
  const getParticipantName = (id?: string | null) => {
    if (!id) return '輪空'; // Bye
    const p = event.participants.find(p => p._id === id);
    return p ? p.name : 'Unknown';
  };

  // Calculate Standings
  const calculateStandings = (group: TournamentGroup): ParticipantStats[] => {
    const stats: Record<string, ParticipantStats> = {};

    group.participants.forEach(pId => {
      stats[pId] = {
        id: pId,
        name: getParticipantName(pId),
        played: 0,
        won: 0,
        lost: 0,
        points: 0
      };
    });

    group.matches.forEach(m => {
      if (m.score1 === 0 && m.score2 === 0 && m.status === 'pending') return; // Skip unplayed

      if (stats[m.player1!]) stats[m.player1!].played++;
      if (stats[m.player2!]) stats[m.player2!].played++;

      if (m.score1 > m.score2) {
        if (stats[m.player1!]) { stats[m.player1!].won++; stats[m.player1!].points += 3; }
        if (stats[m.player2!]) { stats[m.player2!].lost++; }
      } else if (m.score2 > m.score1) {
        if (stats[m.player2!]) { stats[m.player2!].won++; stats[m.player2!].points += 3; }
        if (stats[m.player1!]) { stats[m.player1!].lost++; }
      } else {
        // Draw
        if (stats[m.player1!]) { stats[m.player1!].points += 1; }
        if (stats[m.player2!]) { stats[m.player2!].points += 1; }
      }
    });

    return Object.values(stats).sort((a, b) => b.points - a.points);
  };

  if (!event.tournamentData || event.tournamentData.stage === 'setup') {
    if (readOnly) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <EmojiEventsIcon sx={{ fontSize: 60, color: 'gold', mb: 2 }} />
          <Typography variant="h5" gutterBottom>比賽準備中</Typography>
          <Typography variant="body1" color="text.secondary">
            主辦單位正在設定比賽規則及抽籤，請稍後再查看。
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <EmojiEventsIcon sx={{ fontSize: 60, color: 'gold', mb: 2 }} />
        <Typography variant="h5" gutterBottom>比賽設定</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          此活動已標記為比賽模式。請設定比賽規則。
        </Typography>

        <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4, textAlign: 'left' }}>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>比賽模式</Typography>
            <RadioGroup
              row
              value={startMode}
              onChange={(e) => setStartMode(e.target.value as 'group' | 'knockout')}
            >
              <FormControlLabel value="group" control={<Radio />} label="分組循環賽 + 淘汰賽" />
              <FormControlLabel value="knockout" control={<Radio />} label="直接淘汰賽" />
            </RadioGroup>
          </FormControl>

          {startMode === 'group' && (
            <>
              <TextField
                label="分組數量"
                type="number"
                fullWidth
                value={groupCount}
                onChange={(e) => {
                  const val = e.target.value;
                  setGroupCount(val === '' ? '' : parseInt(val));
                }}
                InputProps={{ inputProps: { min: 1, max: 8 } }}
                sx={{ mb: 2 }}
              />
              <TextField
                label="每組晉級人數"
                type="number"
                fullWidth
                value={advancePerGroup}
                onChange={(e) => {
                  const val = e.target.value;
                  setAdvancePerGroup(val === '' ? '' : parseInt(val));
                }}
                InputProps={{ inputProps: { min: 1, max: 4 } }}
                sx={{ mb: 2 }}
              />
              <TextField
                select
                label="小組賽局數"
                fullWidth
                value={groupBestOf}
                onChange={(e) => setGroupBestOf(parseInt(e.target.value as string))}
                SelectProps={{ native: true }}
                sx={{ mb: 2 }}
              >
                <option value={1}>一局決勝 (1 Set)</option>
                <option value={3}>三局兩勝 (Best of 3)</option>
                <option value={5}>五局三勝 (Best of 5)</option>
              </TextField>
              <TextField
                select
                label="淘汰賽局數"
                fullWidth
                value={knockoutBestOf}
                onChange={(e) => setKnockoutBestOf(parseInt(e.target.value as string))}
                SelectProps={{ native: true }}
                sx={{ mb: 2 }}
              >
                <option value={1}>一局決勝 (1 Set)</option>
                <option value={3}>三局兩勝 (Best of 3)</option>
                <option value={5}>五局三勝 (Best of 5)</option>
              </TextField>
            </>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleGenerateDraw}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {loading ? '生成中...' : (startMode === 'group' ? '生成抽籤及賽程' : '生成淘汰賽對陣')}
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" color="text.secondary">
            參加者人數: {event.participants.length}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ width: '100%' }} variant="h6">
          比賽管理
        </Typography>
        <Box sx={{ display: 'flex' }}>
          {readOnly && (
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          )}
          {!readOnly && event.tournamentData?.stage === 'group' && (
            <IconButton onClick={() => {
              setSettingsAdvancePerGroup(event.tournamentData?.config.advancePerGroup || 2);
              setSettingsOpen(true);
            }}>
              <SettingsIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {event.tournamentData.stage === 'knockout' && (
            <Button
              startIcon={<EmojiEventsIcon />}
              size="small"
              onClick={handleGenerateKnockout}
              color="primary"
              variant="contained"
              sx={{ mr: 1 }}
            >
              下一輪淘汰賽
            </Button>
          )}
          {event.tournamentData.stage === 'group' && (
            <Button
              startIcon={<MilitaryTechIcon />}
              size="small"
              onClick={handleGenerateKnockout}
              color="primary"
              variant="contained"
              sx={{ mr: 1 }}
            >
              進入淘汰賽
            </Button>
          )}
          <Button
            startIcon={<RestartAltIcon />}
            size="small"
            onClick={handleReset}
            color="error"
            sx={{ mr: 1 }}
          >
            重置比賽
          </Button>
          {event.tournamentData.stage === 'group' && (
            <Button
              startIcon={<RefreshIcon />}
              size="small"
              onClick={handleGenerateDraw}
              color="warning"
            >
              重新抽籤
            </Button>
          )}
        </Box>
      )}

      <Paper square sx={{ mb: 2 }}>
        <Tabs
          value={activeSection}
          onChange={(e, v) => setActiveSection(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab value="group" label="分組賽" disabled={event.tournamentData?.config?.groupCount === 0} />
          <Tab
            value="knockout"
            label="淘汰賽"
            disabled={event.tournamentData?.stage !== 'knockout'}
          />
        </Tabs>
      </Paper>

      {activeSection === 'knockout' ? (
        <Box sx={{ display: 'flex', overflowX: 'auto', gap: 3, p: 2, alignItems: 'flex-start' }}>
          {event.tournamentData.knockout.map((round) => (
            <Box key={round._id} sx={{ minWidth: 280, width: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 2
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
                  {round.name}
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {round.matches.map((match) => (
                  <Paper
                    key={match._id}
                    elevation={1}
                    sx={{
                      p: 0,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2
                    }}
                  >
                    {/* Player 1 */}
                    <Box
                      sx={{
                        p: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        bgcolor: (match.winner === match.player1) || (match.score1 > match.score2) ? 'action.selected' : 'transparent',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: (match.winner === match.player1) || (match.score1 > match.score2) ? 'bold' : 'normal',
                          color: (match.winner === match.player1) || (match.score1 > match.score2) ? 'text.primary' : 'text.secondary'
                        }}
                      >
                        {getParticipantName(match.player1)}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                          color: (match.winner === match.player1) || (match.score1 > match.score2) ? 'primary.main' : 'text.disabled'
                        }}
                      >
                        {match.score1}
                        {match.sets && match.sets.length > 0 && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 'normal', opacity: 0.7 }}>
                            ({match.sets.map(s => s.score1).join(', ')})
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {/* Player 2 */}
                    <Box
                      sx={{
                        p: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        bgcolor: (match.winner && match.winner === match.player2) || (match.score2 > match.score1) ? 'action.selected' : 'transparent'
                      }}
                    >
                      {match.player2 ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: (match.winner && match.winner === match.player2) || (match.score2 > match.score1) ? 'bold' : 'normal',
                              color: (match.winner && match.winner === match.player2) || (match.score2 > match.score1) ? 'text.primary' : 'text.secondary'
                            }}
                          >
                            {getParticipantName(match.player2)}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              color: (match.winner && match.winner === match.player2) || (match.score2 > match.score1) ? 'primary.main' : 'text.disabled'
                            }}
                          >
                            {match.score2}
                            {match.sets && match.sets.length > 0 && (
                              <Typography component="span" variant="caption" sx={{ ml: 0.5, fontWeight: 'normal', opacity: 0.7 }}>
                                ({match.sets.map(s => s.score2).join(', ')})
                              </Typography>
                            )}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          (輪空)
                        </Typography>
                      )}
                    </Box>

                    {/* Location Display */}
                    {match.location && (
                      <Box sx={{
                        px: 1,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#fafafa',
                        borderTop: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {match.location}
                        </Typography>
                      </Box>
                    )}

                    {/* Action Bar for Admin */}
                    {!readOnly && match.player2 && (
                      <Box sx={{ bgcolor: '#f5f5f5', p: 0.5, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          size="small"
                          onClick={() => openEditMatchDialog(match, '', round._id)}
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          sx={{ fontSize: '0.75rem', py: 0, minHeight: 24 }}
                        >
                          更新比分
                        </Button>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}
          {event.tournamentData.knockout.length === 0 && (
            <Box sx={{ width: '100%', textAlign: 'center', mt: 4 }}>
              <Typography color="text.secondary">
                尚無淘汰賽數據
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Paper square>
            <Tabs
              value={activeGroupTab}
              onChange={(e, v) => setActiveGroupTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
            >
              {event.tournamentData.groups.map((group, index) => (
                <Tab
                  key={group._id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Group {group.name}</span>
                      {!readOnly && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGroupName({ groupId: group._id, name: group.name });
                          }}
                          sx={{ p: 0.25, ml: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>

          {event.tournamentData.groups.map((group, index) => (
            <div key={group._id} role="tabpanel" hidden={activeGroupTab !== index}>
              {activeGroupTab === index && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {/* Standings Table */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">積分榜</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>姓名</TableCell>
                                <TableCell align="center">賽</TableCell>
                                <TableCell align="center">勝</TableCell>
                                <TableCell align="center">負</TableCell>
                                <TableCell align="center">分</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {calculateStandings(group).map((stat) => (
                                <TableRow key={stat.id}>
                                  <TableCell>{stat.name}</TableCell>
                                  <TableCell align="center">{stat.played}</TableCell>
                                  <TableCell align="center">{stat.won}</TableCell>
                                  <TableCell align="center">{stat.lost}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{stat.points}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>

                    {/* Match List */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">賽程表</Typography>
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          {group.matches.map((match, mIndex) => (
                            <Box key={match._id} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ width: '40%', textAlign: 'right', pr: 1 }}>
                                  {getParticipantName(match.player1)}
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6" sx={{ minWidth: 20, textAlign: 'center' }}>{match.score1}</Typography>
                                    <Typography>-</Typography>
                                    <Typography variant="h6" sx={{ minWidth: 20, textAlign: 'center' }}>{match.score2}</Typography>
                                  </Box>
                                  {match.sets && match.sets.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {match.sets.map(s => `${s.score1}-${s.score2}`).join(', ')}
                                    </Typography>
                                  )}
                                </Box>

                                <Typography variant="body2" sx={{ width: '40%', textAlign: 'left', pl: 1 }}>
                                  {getParticipantName(match.player2)}
                                </Typography>

                                {!readOnly && (
                                  <Box sx={{ display: 'flex' }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMoveMatch(match._id, group._id, 'up')}
                                      disabled={loading || mIndex === 0}
                                    >
                                      <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleMoveMatch(match._id, group._id, 'down')}
                                      disabled={loading || mIndex === group.matches.length - 1}
                                    >
                                      <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditMatchDialog(match, group._id)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                              {/* Location Display */}
                              {match.location && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, justifyContent: 'center' }}>
                                  <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {match.location}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </div>
          ))}
        </>
      )}

      {/* Edit Match Dialog */}
      <Dialog open={!!editingMatch} onClose={() => setEditingMatch(null)}>
        <DialogTitle>更新比分</DialogTitle>
        <DialogContent>
          {editingMatch && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 250 }}>
              {editScores.sets && editScores.sets.length > 0 ? (
                // Multi-set editing
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {getParticipantName(editingMatch.match.player1)} vs {getParticipantName(editingMatch.match.player2)} (局數: {editScores.sets.length})
                  </Typography>
                  {editScores.sets.map((set, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 'bold' }}>Set {index + 1}</Typography>
                      <TextField
                        type="number"
                        size="small"
                        label={getParticipantName(editingMatch.match.player1)}
                        value={set.score1}
                        onChange={(e) => {
                          const newSets = [...(editScores.sets || [])];
                          newSets[index] = { ...newSets[index], score1: e.target.value === '' ? '' : Number(e.target.value) };
                          setEditScores({ ...editScores, sets: newSets });
                        }}
                        sx={{ flex: 1 }}
                        inputProps={{ style: { textAlign: 'center' } }}
                      />
                      <Typography>-</Typography>
                      <TextField
                        type="number"
                        size="small"
                        label={getParticipantName(editingMatch.match.player2)}
                        value={set.score2}
                        onChange={(e) => {
                          const newSets = [...(editScores.sets || [])];
                          newSets[index] = { ...newSets[index], score2: e.target.value === '' ? '' : Number(e.target.value) };
                          setEditScores({ ...editScores, sets: newSets });
                        }}
                        sx={{ flex: 1 }}
                        inputProps={{ style: { textAlign: 'center' } }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                // Single set editing
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body1">{getParticipantName(editingMatch.match.player1)}</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={editScores.score1}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditScores(prev => ({ ...prev, score1: val === '' ? '' : parseInt(val) }));
                      }}
                      sx={{ width: 80 }}
                      inputProps={{ style: { textAlign: 'center' } }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body1">{getParticipantName(editingMatch.match.player2)}</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={editScores.score2}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditScores(prev => ({ ...prev, score2: val === '' ? '' : parseInt(val) }));
                      }}
                      sx={{ width: 80 }}
                      inputProps={{ style: { textAlign: 'center' } }}
                    />
                  </Box>
                </>
              )}

              <TextField
                label="比賽地點"
                size="small"
                fullWidth
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                placeholder="輸入比賽場地"
                InputProps={{
                  startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMatch(null)} disabled={isSavingScore}>取消</Button>
          <Button onClick={handleSaveScore} variant="contained" disabled={isSavingScore}>
            {isSavingScore ? <CircularProgress size={24} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirmDialogData?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>{confirmDialogData?.message}</Typography>

          {/* Custom Selection UI for Group Stage */}
          {event.tournamentData?.stage === 'group' && confirmDialogData?.actionType === 'generateKnockout' ? (
            <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
              {event.tournamentData.groups.map(group => {
                const standings = calculateStandings(group);
                const selected = selectedQualifiers[group._id] || [];
                const advanceCount = Number(event.tournamentData?.config.advancePerGroup) || 2;

                return (
                  <Box key={group._id} sx={{ mb: 2, border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold">Group {group.name}</Typography>
                      <Typography variant="caption" color={selected.length === advanceCount ? 'success.main' : 'error.main'}>
                        已選: {selected.length} / {advanceCount}
                      </Typography>
                    </Box>
                    <List dense disablePadding>
                      {standings.map((p, idx) => (
                        <ListItem
                          key={p.id}
                          disablePadding
                        >
                          <ListItemButton
                            onClick={() => {
                              const currentSelected = [...selected];
                              if (currentSelected.includes(p.id)) {
                                // Deselect
                                const newSelected = currentSelected.filter(id => id !== p.id);
                                setSelectedQualifiers(prev => ({ ...prev, [group._id]: newSelected }));
                              } else {
                                const newSelected = [...currentSelected, p.id];
                                setSelectedQualifiers(prev => ({ ...prev, [group._id]: newSelected }));
                              }
                            }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={selected.includes(p.id)}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${idx + 1}. ${p.name}`}
                              secondary={`積分: ${p.points} | 淨勝: ${p.won}`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
              {confirmDialogData?.qualifiers?.length === 0 ? (
                <Typography color="error">沒有符合晉級條件的參賽者 (請確認比賽是否已完成)</Typography>
              ) : (
                <Grid container spacing={1}>
                  {confirmDialogData?.qualifiers?.map((q, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">{q.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{q.info}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={loading}>取消</Button>
          <Button
            onClick={() => {
              if (confirmDialogData?.actionType === 'generateKnockout') {
                executeKnockoutGeneration();
              }
            }}
            variant="contained"
            color="primary"
            disabled={loading || (event.tournamentData?.stage === 'knockout' && confirmDialogData?.qualifiers?.length === 0)}
          >
            {loading ? <CircularProgress size={24} /> : '確認生成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>比賽設定調整</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="每組晉級人數"
              type="number"
              fullWidth
              value={settingsAdvancePerGroup}
              onChange={(e) => {
                const val = e.target.value;
                setSettingsAdvancePerGroup(val === '' ? '' : parseInt(val));
              }}
              InputProps={{ inputProps: { min: 1, max: 4 } }}
              helperText="此設定將影響晉級名單的預設選擇"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} disabled={loading}>取消</Button>
          <Button onClick={handleUpdateSettings} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Name Edit Dialog */}
      <Dialog open={!!editingGroupName} onClose={() => setEditingGroupName(null)} maxWidth="xs" fullWidth>
        <DialogTitle>修改分組名稱</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="分組名稱"
              fullWidth
              value={editingGroupName?.name || ''}
              onChange={(e) => setEditingGroupName(prev => prev ? { ...prev, name: e.target.value } : null)}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingGroupName(null)} disabled={loading}>取消</Button>
          <Button onClick={handleUpdateGroupName} variant="contained" disabled={loading || !editingGroupName?.name.trim()}>
            {loading ? <CircularProgress size={24} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notification?.severity} onClose={() => setNotification(null)}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TournamentManager;

